/**
 * Default view-mode heuristic for a Dependency Viewer tab (F09/B9).
 *
 * A hairball graph was the reported bug: opening "Dependencies" always
 * forced graph mode, which is unreadable past a couple dozen nodes — and
 * tables (typically the widest fan-out object in a schema) hit that
 * threshold constantly. Tree stays readable at any size, so:
 *   - a table always defaults to tree, regardless of dependency count;
 *   - anything else defaults to tree once its dependency count exceeds
 *     `LARGE_DEPENDENCY_COUNT_THRESHOLD`;
 *   - everything else keeps the existing graph default — it's still the
 *     more exploratory view, and forcing tree on every small object would
 *     just move the readability problem, not fix it.
 *
 * This is intentionally one-directional. The dependency COUNT isn't known
 * until the catalog fetch resolves, so callers apply this twice:
 *   1. At tab-open time, with count 0 — this only ever affects the table
 *      branch (which ignores the count), so a non-table object still opens
 *      provisionally on graph.
 *   2. Once the fetch resolves with the real count, to UPGRADE a still-graph
 *      tab to tree if the count turns out to be large. Never use this to
 *      downgrade a mode the user (or an earlier call) already set to tree.
 */
import type { DependencyObjectType, DependencyViewMode } from '../../core/types/workspaceTypes';

/** Dependency count above which even a non-table object defaults to tree. */
export const LARGE_DEPENDENCY_COUNT_THRESHOLD = 20;

export function resolveDefaultDependencyViewMode(
  objectType: DependencyObjectType,
  totalDependencyCount: number
): Extract<DependencyViewMode, 'tree' | 'graph'> {
  if (objectType === 'table') return 'tree';
  if (totalDependencyCount > LARGE_DEPENDENCY_COUNT_THRESHOLD) return 'tree';
  return 'graph';
}
